import * as React from "react";
import { Dropdown, MessageBar, MessageBarType, Stack, TextField, Toggle } from "office-ui-fabric-react";
import Utils from "../../../utils";
import { Constants } from "../../../utils/constants";
import { ConstraintType, IBookingConstraint, ResourceType } from "../../../types/Booking.types";
import { Label } from "@fluentui/react";

export enum RestrictionUpdate {
    Site = 1,
    Location = 2,
    Room = 3,
    Global = 4
}

export interface IRestrictionsSectionProps {
    resourceId: number;
    updateType: RestrictionUpdate;
    deskBookingConstraint?: IBookingConstraint;
    roomBookingConstraint?: IBookingConstraint;
    roomCheckIn?: boolean;
    onChange: (constraints:{ deskBookingConstraint?: IBookingConstraint, roomBookingConstraint?: IBookingConstraint }, roomCheckIn?: boolean) => void;
}

export const RestrictionsSection = (props: IRestrictionsSectionProps) => {

    // Booking constraints
    const [ deskConstraint, setDeskConstraint ] = React.useState<IBookingConstraint|undefined>(props.deskBookingConstraint);
    const [ useDeskConstraint, setUseDeskConstraint ] = React.useState(Boolean(props.deskBookingConstraint));
    const [ roomConstraint, setRoomConstraint ] = React.useState<IBookingConstraint|undefined>(props.roomBookingConstraint);
    const [useRoomConstraint, setUseRoomConstraint] = React.useState(Boolean(props.roomBookingConstraint));
    const [enableCheckIn, setEnableCheckIn] = React.useState<boolean|undefined>(props.roomCheckIn);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const getDefaultConstraint = React.useCallback(Utils.getDefaultConstraint, []);
    const constraintParams: (number|null)[] = [null, null, null];
    // const constraintProps = ["SiteId", "LocationId", "RoomId"];
    constraintParams[props.updateType - 1] = props.resourceId;
    // const constraintUpdateProp = constraintProps[props.updateType - 1];

    const constraintOptions = React.useMemo(() => (Constants.ConstraintTypeOptions), []);

    // On Update
    React.useEffect(() => {
        props.onChange({ deskBookingConstraint: deskConstraint, roomBookingConstraint: roomConstraint}, enableCheckIn);
    }, [deskConstraint, roomConstraint, enableCheckIn]);

    return (
        <>
            <MessageBar messageBarType={MessageBarType.info}>
                Time constraints be set Globally, or at Site, Location or Room level. 
            </MessageBar>
            { props.updateType !== RestrictionUpdate.Room && (
                <>
                    <Toggle label="Desk Booking Time Constraint" onText="On" offText="None" checked={useDeskConstraint} onChange={(e, checked) => {
                        if (!checked) {
                            setDeskConstraint(undefined);
                        } else {
                            setDeskConstraint(deskConstraint || getDefaultConstraint(ResourceType.Desk));
                        }
                        setUseDeskConstraint(checked || false)
                    }} />
                    { useDeskConstraint === true && (
                        <Stack horizontal horizontalAlign="center" tokens={{childrenGap: 10}}>
                            <TextField type="number" value={deskConstraint?.Number?.toString()} onChange={(e, newValue) => {
                                const constraint = {...deskConstraint} || getDefaultConstraint(ResourceType.Desk, ...constraintParams);
                                if (newValue) {
                                    const numVal = parseInt(newValue, 10);
                                    constraint.Number = numVal;
                                } else {
                                    constraint.Number = 0;
                                }
                                setDeskConstraint(constraint);
                            }} />
                            <Dropdown options={constraintOptions} selectedKey={deskConstraint?.Type} onChange={(e, option) => {
                                const constraint = {...deskConstraint} || getDefaultConstraint(ResourceType.Desk, ...constraintParams);
                                constraint.Type = option?.key as ConstraintType;
                                setDeskConstraint(constraint);
                            }} />
                        </Stack>
                    )}
                </>
            )}
            <Toggle label="Room Booking Time Constraint" onText="On" offText="None" checked={useRoomConstraint} onChange={(e, checked) => {
                if (!checked) {
                    setRoomConstraint(undefined);
                } else {
                    setRoomConstraint(roomConstraint || getDefaultConstraint(ResourceType.Room, ...constraintParams));
                }
                setUseRoomConstraint(checked || false)
            }} />
            { useRoomConstraint === true && (
                <Stack horizontal horizontalAlign="center" tokens={{childrenGap: 10}}>
                    <TextField type="number" value={roomConstraint?.Number?.toString()} onChange={(e, newValue) => {
                        const constraint = {...roomConstraint} || getDefaultConstraint(ResourceType.Room, ...constraintParams);
                        if (newValue) {
                            const numVal = parseInt(newValue, 10);
                            constraint.Number = numVal;
                        } else {
                            constraint.Number = 0;
                        }
                        setRoomConstraint(constraint);
                    }} />
                    <Dropdown options={constraintOptions} selectedKey={roomConstraint?.Type} onChange={(e, option) => {
                        const constraint = {...roomConstraint} || getDefaultConstraint(ResourceType.Room, ...constraintParams);
                        constraint.Type = option?.key as ConstraintType;
                        setRoomConstraint(constraint);
                    }} />
                </Stack>
            )}
            {props.updateType != RestrictionUpdate.Global && (<>
                <Label>Meeting Room Check-In and Auto-Cancellation</Label>
                <MessageBar messageBarType={MessageBarType.info}>
                    You can require that users check-in to meeting rooms via the app (or using QR codes). When enabled, if users do not check-in, their meeting will be automatically cancelled. Users have 15 minutes before and after their meeting during which they can check-in.
                </MessageBar>
                <Toggle onText="Enabled" offText="Disabled" checked={enableCheckIn} onChange={(e, checked) => setEnableCheckIn(checked)} />
            </>)}
        </>
    );
};